/**
 * HeroSection Component with Image/Video Slider
 * 
 * This component creates a dynamic hero section with a slider that supports both images and videos.
 * 
 * To add slides:
 * 1. Add a new object to the 'slides' array
 * 2. Set 'type' to 'image' or 'video'
 * 3. For images: Set 'media' to an image URL (can be external URL or local path like '/images/hero1.jpg')
 * 4. For videos: Set 'media' to a video URL (local path like '/videos/hero-video.mp4')
 *    - Place video files in the public/videos/ directory
 *    - Videos will autoplay, loop, and be muted
 * 5. Add title, subtitle, and description for each slide
 * 
 * Features:
 * - Auto-play slides (changes every 8 seconds)
 * - Manual navigation with arrow buttons
 * - Dot indicators for direct slide access
 * - Smooth transitions between slides
 * - Supports both images and videos
 * - Responsive design for all screen sizes
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './HeroSection.css';

// Slides data - move outside component to avoid dependency issues
const slides = [
  {
    id: 1,
    type: 'image', // 'image' or 'video'
    media: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&h=1080&fit=crop',
    title: 'Welcome to Vibha Corporation',
    subtitle: 'Your Trusted Partner in Construction & Real Estate Excellence',
    description: 'With nearly two decades of proven expertise in the Indian real estate and construction sector, Vibha Corporation stands as a beacon of reliability, innovation, and quality in Tamil Nadu\'s construction landscape.'
  },
  {
    id: 2,
    type: 'image',
    media: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=1920&h=1080&fit=crop',
    title: 'Building Excellence Since 2005',
    subtitle: 'Transforming Visions into Reality',
    description: 'Led by industry veteran Sundar Rajan Jagannathan (SRJ), we bring unparalleled experience from handling transactions worth over ₹5,500 Crores across South India.'
  },
  {
    id: 3,
    type: 'image',
    media: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&h=1080&fit=crop',
    title: 'Quality Construction Projects',
    subtitle: 'Excellence in Every Detail',
    description: 'From residential complexes to commercial developments, we deliver projects that exceed expectations with uncompromising quality and attention to detail.'
  },
  // Example video slide (uncomment and add your video URL)
  // {
  //   id: 4,
  //   type: 'video',
  //   media: '/videos/hero-video.mp4', // Add your video file to public/videos/
  //   title: 'See Our Work in Action',
  //   subtitle: 'Experience Our Construction Excellence',
  //   description: 'Watch how we transform ideas into magnificent structures that stand the test of time.'
  // }
];

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const videoRefs = useRef([]);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000); // Change slide every 8 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, slides.length]);

  // Handle video playback
  useEffect(() => {
    const currentVideo = videoRefs.current[currentSlide];
    const currentSlideData = slides[currentSlide];
    
    // Pause all videos first
    videoRefs.current.forEach((videoRef) => {
      if (videoRef) {
        videoRef.pause();
        videoRef.currentTime = 0;
      }
    });
    
    // Play current video if it's a video slide
    if (currentSlideData && currentSlideData.type === 'video' && currentVideo) {
      const playPromise = currentVideo.play();
      if (playPromise !== undefined) {
        playPromise.catch(() => {
          // Auto-play might be blocked by browser
          console.log('Video autoplay blocked');
        });
      }
    }
  }, [currentSlide]);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    // Resume auto-play after manual navigation
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToPrev = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <section className="hero-section">
      {/* Slider Background Container */}
      <div className="hero-background-container">
        <AnimatePresence mode="wait">
          {slides.map((slide, index) => (
            index === currentSlide && (
              <motion.div
                key={slide.id}
                className="hero-background-slide"
                initial={{ opacity: 0, scale: 1.1 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.1 }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
              >
                {slide.type === 'image' ? (
                  <div
                    className="slide-image"
                    style={{ backgroundImage: `url(${slide.media})` }}
                  />
                ) : (
                  <video
                    ref={(el) => (videoRefs.current[index] = el)}
                    className="slide-video"
                    src={slide.media}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    onError={(e) => {
                      console.error('Video loading error:', e);
                      // Fallback to a placeholder image if video fails to load
                    }}
                  />
                )}
              </motion.div>
            )
          ))}
        </AnimatePresence>
      </div>

      {/* Professional overlay */}
      <div className="hero-overlay" />

      {/* Navigation Arrows */}
      <button
        className="slider-nav slider-nav-prev"
        onClick={goToPrev}
        aria-label="Previous slide"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      <button
        className="slider-nav slider-nav-next"
        onClick={goToNext}
        aria-label="Next slide"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Main content container */}
      <div className="hero-container">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            className="hero-content"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 50 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {/* Company branding */}
            <motion.div
              className="hero-brand"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="brand-logo">
                <span className="brand-text">VIBHA CORPORATION</span>
                <span className="brand-tagline">& CO.</span>
              </div>
            </motion.div>

            {/* Main headline with dynamic content */}
            <motion.div
              className="hero-headline"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <h1 className="headline-primary">{currentSlideData.title}</h1>
              <h2 className="headline-secondary">{currentSlideData.subtitle}</h2>
            </motion.div>

            {/* Description */}
            <motion.p
              className="hero-description"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {currentSlideData.description}
            </motion.p>

            {/* Service highlights */}
            <motion.div
              className="hero-cta"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <div className="service-highlights">
                <span className="highlight-item">🏗️ Construction Excellence</span>
                <span className="highlight-item">🏢 Property Development</span>
                <span className="highlight-item">📋 Real Estate Consultancy</span>
              </div>
              <div className="mission-statement">
                "Building Dreams, Creating Value, Ensuring Satisfaction"
              </div>
            </motion.div>

            {/* Key statistics */}
            <motion.div
              className="hero-metrics"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
            >
              <div className="metric-item">
                <div className="metric-number">20+</div>
                <div className="metric-label">Years Experience</div>
              </div>
              <div className="metric-separator" />
              <div className="metric-item">
                <div className="metric-number">₹5,500+</div>
                <div className="metric-label">Crores in Transactions</div>
              </div>
              <div className="metric-separator" />
              <div className="metric-item">
                <div className="metric-number">10+</div>
                <div className="metric-label">Cities Served</div>
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Slider Indicators */}
      <div className="slider-indicators">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            className={`slider-dot ${index === currentSlide ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2 }}
      >
        <div className="scroll-mouse">
          <div className="scroll-wheel"></div>
        </div>
        <div className="scroll-text">Scroll to explore</div>
      </motion.div>

      {/* Floating Elements */}
      <motion.div
        className="floating-element element-1"
        animate={{
          y: [0, -30, 0],
          rotate: [0, 180, 360]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "linear"
        }}
      >
        🏗️
      </motion.div>

      <motion.div
        className="floating-element element-2"
        animate={{
          y: [0, 40, 0],
          x: [0, 20, 0]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      >
        🏢
      </motion.div>

      <motion.div
        className="floating-element element-3"
        animate={{
          y: [0, -25, 0],
          rotate: [0, -180, -360]
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: "linear"
        }}
      >
        📋
      </motion.div>
    </section>
  );
};

export default HeroSection;
